import {
  PointerEvent,
  WheelEvent as ReactWheelEvent,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import {
  ImageViewerActionStack,
  ImageViewerIconButton,
  ImageViewerMainImage,
  ImageViewerOverlay,
  ImageViewerPanSurface,
  ImageViewerStage,
  ImageViewerThumbButton,
  ImageViewerThumbList,
  ImageViewerThumbRail
} from "./styles";

export interface ImageViewerProps {
  images: string[];
  initialIndex?: number;
  open: boolean;
  onClose: () => void;
}

const normalizeIndex = (index: number, imagesLength: number) => {
  if (imagesLength <= 0) {
    return 0;
  }

  return Math.min(Math.max(index, 0), imagesLength - 1);
};

export const ImageViewer = ({
  images,
  initialIndex = 0,
  open,
  onClose
}: ImageViewerProps) => {
  const [activeIndex, setActiveIndex] = useState(() =>
    normalizeIndex(initialIndex, images.length)
  );
  const [zoom, setZoom] = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const thumbnailRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const resetImagePosition = useCallback(() => {
    setZoom(1);
    setZoomOrigin({ x: 50, y: 50 });
    setImageOffset({ x: 0, y: 0 });
    setIsDragging(false);
  }, []);

  const closeViewer = useCallback(() => {
    onClose();
    resetImagePosition();
  }, [onClose, resetImagePosition]);

  useEffect(() => {
    if (!open) return;

    setActiveIndex(normalizeIndex(initialIndex, images.length));
    resetImagePosition();
  }, [images.length, initialIndex, open, resetImagePosition]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeViewer();
        return;
      }

      if (images.length <= 1) return;

      if (event.key === "ArrowLeft") {
        setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      }

      if (event.key === "ArrowRight") {
        setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeViewer, images.length, open]);

  useEffect(() => {
    if (!open) return;

    resetImagePosition();
    thumbnailRefs.current[activeIndex]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center"
    });
  }, [activeIndex, open, resetImagePosition]);

  const showPreviousImage = () => {
    if (images.length <= 1) return;
    setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const showNextImage = () => {
    if (images.length <= 1) return;
    setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const getPointerOrigin = useCallback((clientX: number, clientY: number) => {
    const imageElement = imageRef.current;
    if (!imageElement) {
      return { x: 50, y: 50 };
    }

    const rect = imageElement.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return { x: 50, y: 50 };
    }

    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;

    return {
      x: Math.min(100, Math.max(0, Number(x.toFixed(2)))),
      y: Math.min(100, Math.max(0, Number(y.toFixed(2))))
    };
  }, []);

  const applyZoomDelta = useCallback(
    (deltaY: number, clientX: number, clientY: number) => {
      setZoomOrigin(getPointerOrigin(clientX, clientY));
      setIsDragging(false);
      setZoom((prev) => {
        const rawStep = Math.abs(deltaY) < 20 ? Math.abs(deltaY) * 0.02 : 0.2;
        const step = Math.max(0.05, Math.min(0.3, Number(rawStep.toFixed(2))));
        const nextZoom = deltaY < 0 ? prev + step : prev - step;
        const normalizedZoom = Math.min(
          4,
          Math.max(1, Number(nextZoom.toFixed(2)))
        );

        if (normalizedZoom === 1) {
          setImageOffset({ x: 0, y: 0 });
          setIsDragging(false);
        }

        return normalizedZoom;
      });
    },
    [getPointerOrigin]
  );

  const handleWheelZoom = (event: ReactWheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    applyZoomDelta(event.deltaY, event.clientX, event.clientY);
  };

  useEffect(() => {
    if (!open) return;

    const stageElement = stageRef.current;
    if (!stageElement) return;

    const handleTrackpadPinch = (event: globalThis.WheelEvent) => {
      if (!event.ctrlKey) return;

      event.preventDefault();
      event.stopPropagation();
      applyZoomDelta(event.deltaY, event.clientX, event.clientY);
    };

    stageElement.addEventListener("wheel", handleTrackpadPinch, {
      passive: false
    });

    return () => {
      stageElement.removeEventListener("wheel", handleTrackpadPinch);
    };
  }, [applyZoomDelta, open]);

  const updateZoom = (direction: "in" | "out") => {
    setZoomOrigin({ x: 50, y: 50 });
    setIsDragging(false);
    setZoom((prev) => {
      const nextZoom = direction === "in" ? prev + 0.2 : prev - 0.2;
      const normalizedZoom = Math.min(
        4,
        Math.max(1, Number(nextZoom.toFixed(2)))
      );

      if (normalizedZoom === 1) {
        setImageOffset({ x: 0, y: 0 });
      }

      return normalizedZoom;
    });
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (zoom <= 1) return;

    event.preventDefault();
    dragStartRef.current = {
      x: event.clientX - imageOffset.x,
      y: event.clientY - imageOffset.y
    };
    setIsDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!isDragging || zoom <= 1) return;

    event.preventDefault();
    setImageOffset({
      x: event.clientX - dragStartRef.current.x,
      y: event.clientY - dragStartRef.current.y
    });
  };

  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setIsDragging(false);
  };

  if (!open || images.length === 0) {
    return null;
  }

  return createPortal(
    <ImageViewerOverlay onClick={closeViewer}>
      <ImageViewerActionStack>
        <ImageViewerIconButton
          type="button"
          $variant="close"
          onClick={closeViewer}
          aria-label="Fechar visualizador"
        >
          <i className="pi pi-times" style={{ fontSize: "18px" }} />
        </ImageViewerIconButton>
        <ImageViewerIconButton
          type="button"
          $variant="zoom"
          onClick={(event) => {
            event.stopPropagation();
            updateZoom("in");
          }}
          aria-label="Aumentar zoom"
        >
          <i className="pi pi-search-plus" style={{ fontSize: "20px" }} />
        </ImageViewerIconButton>
        <ImageViewerIconButton
          type="button"
          $variant="zoom"
          onClick={(event) => {
            event.stopPropagation();
            updateZoom("out");
          }}
          aria-label="Diminuir zoom"
        >
          <i className="pi pi-search-minus" style={{ fontSize: "20px" }} />
        </ImageViewerIconButton>
      </ImageViewerActionStack>
      {images.length > 1 && (
        <>
          <ImageViewerIconButton
            type="button"
            $variant="left"
            onClick={(event) => {
              event.stopPropagation();
              showPreviousImage();
            }}
            aria-label="Imagem anterior"
          >
            <i className="pi pi-angle-left" style={{ fontSize: "24px" }} />
          </ImageViewerIconButton>
          <ImageViewerIconButton
            type="button"
            $variant="right"
            onClick={(event) => {
              event.stopPropagation();
              showNextImage();
            }}
            aria-label="Próxima imagem"
          >
            <i className="pi pi-angle-right" style={{ fontSize: "24px" }} />
          </ImageViewerIconButton>
        </>
      )}
      <ImageViewerStage
        ref={stageRef}
        $isDraggable={zoom > 1}
        onClick={(event) => event.stopPropagation()}
        onWheel={handleWheelZoom}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <ImageViewerPanSurface $x={imageOffset.x} $y={imageOffset.y}>
          <ImageViewerMainImage
            ref={imageRef}
            src={images[activeIndex]}
            alt="Imagem ampliada"
            $zoom={zoom}
            $originX={zoomOrigin.x}
            $originY={zoomOrigin.y}
            $isDragging={isDragging}
          />
        </ImageViewerPanSurface>
      </ImageViewerStage>
      {images.length > 1 && (
        <ImageViewerThumbRail onClick={(event) => event.stopPropagation()}>
          <ImageViewerThumbList>
            {images.map((imageUrl, index) => (
              <ImageViewerThumbButton
                key={`${imageUrl}-${index}`}
                ref={(element) => {
                  thumbnailRefs.current[index] = element;
                }}
                type="button"
                $active={activeIndex === index}
                onClick={() => setActiveIndex(index)}
                aria-label={`Abrir imagem ${index + 1}`}
              >
                <img src={imageUrl} alt={`Miniatura ${index + 1}`} />
              </ImageViewerThumbButton>
            ))}
          </ImageViewerThumbList>
        </ImageViewerThumbRail>
      )}
    </ImageViewerOverlay>,
    document.body
  );
};
